import React from "react";
import NavBarItemDropdownMenu from ".";
import { SVGTopNavDirectoryExpand } from "../../../assets/icons/SVGTopNavDirectoryExpand";
import { constructNavColumnWithHeading, constructNavMultipleColumnsWithHeading, constructPrefixNavButtonsWithIcon, constructGallery } from "./component";
import { UrlService } from "../../../core/services/UrlService";
import { Link } from "gatsby";
import { SVGMenu } from "./icon/SVGMenu";
import { useSelector, useStore, useDispatch } from "react-redux";
import { action } from "../../../../wrap-with-provider";
import { SVGCloseCross } from "../../../assets/icons/SVGCloseCross";
import { deactivateNavigationDrawer, activateNavigationDrawer } from "./store/action";
import ListGroup from "./ListGroup";

type NavBarItemStyle = 'link' | 'dropdown-default' | 'dropdown-one-after-another' | 'dropdown-columns';

export interface NavBarItem {
    id: string,
    name: string,
    style: NavBarItemStyle,
    href?: string,
    meta?: {
        newBadge?: boolean,
        ga?: {
            category: string,
            action: string,
            label: string,
        }
    },
    children?: any[],
}

const Brand = () => {
    return (
        <Link className="nav-bar-component_brand-icon" to="https://www.finddoc.com/">
            <img
                src={UrlService.getImageUrl('logo.png')}
                srcSet={`${UrlService.getImageUrl('logo.png')} 1x, ${UrlService.getImageUrl('logo@2x.png')} 2x, ${UrlService.getImageUrl('logo@3x.png')} 3x`}
                alt="FindDoc"
            ></img>
        </Link>
    );
};

export const construct = (data: NavBarItem[], mobileData: any) => {
    const willShowNavigationDrawer = useSelector<any, any>(
        (state) => state?.dropdownReducer.willShowNavigationDrawer
    );

    const [shouldShowOverlay, setShouldShowOverlay] = React.useState(false);
    const [shouldShowDrawer, setShouldShowDrawer] = React.useState(false);
    const [click, addClick] = React.useState(0);

    React.useLayoutEffect(() => {
        if (willShowNavigationDrawer) {
            setShouldShowOverlay(true);
            setTimeout(() => {
                setShouldShowDrawer(true);
            }, 1)
        } else {
            setShouldShowDrawer(false);
            setTimeout(() => {
                setShouldShowOverlay(false);
            }, 250)
        }
    }, [click]);

    const activate = () => {
        addClick(click+1);
        action(activateNavigationDrawer());
    }
    
    const deactivate = () => {
        addClick(click+1);
        action(deactivateNavigationDrawer());
    }
    return (
        <>
            <nav className="navbar-component_desktop-menu">
                <div className="container">
                    <Brand />
                    <ul className="nav-ul">
                        { data.map(x => constructDesktopNavBarItem(x)) }
                    </ul>
                </div>
            </nav>

            <nav className="navbar-component_mobile-menu">
                    <button className="navbar-component_mobile-menu-toggle" aria-label="Toggle mobile menu" onClick={activate}>
                        <div className="navbar-component_square-icon">
                            <SVGMenu />
                        </div>
                    </button>
                    <Brand />
                    <div className={`navbar-component_mobile-drawer ${shouldShowDrawer ? 'show' : ''}`} style={shouldShowOverlay ? {display: 'block'} : {display: 'none'}}>
                        <div className="navbar-component_mobile-drawer-backdrop" onClick={deactivate}></div>
                        <div className="navbar-component_mobile-drawer-content">
                            <div className="navbar-component_mobile-drawer-content-close" onClick={() => action(deactivateNavigationDrawer())}>
                                <div className="svg-icon">
                                    <SVGCloseCross />
                                </div>
                            </div>
                            <div className="navbar-component_mobile-drawer-content-scroll">
                                <ul className="navbar-component_mobile-drawer-content-scroll-list">
                                    { mobileData.map(group => <ListGroup {...group} key={group.heading}/>) }
                                </ul>
                            </div>
                        </div>
                    </div>
                </nav>
            </>
    );
}

export const constructDesktopNavBarItem = (data: NavBarItem) => {
    switch(data.style) { 
        case 'link':
            return constructLink(data);
        case 'dropdown-default':
            return constructDropDownDefault(data);
        case 'dropdown-columns':
            return constructDropDownTwoColumns(data);
        case 'dropdown-one-after-another':
            return constructDropDownOneAfterAnother(data);
    }
};

const constructLink = (data: NavBarItem) => {
    const prop = {
        id: `nav-bar-${data.id}`,
        label: data.name,
        isShowNewBadge: data.meta?.newBadge ?? false,
    };

    return (
        <li className="dropdown-nav-item" key={data.id}>
            <a className="nav-link" href={data.href}>{ data.name }</a>
        </li>
    )
};

const constructDropDownDefault = (data: NavBarItem) => {
    const prop = {
        id: `nav-bar-${data.id}`,
        key: `nav-bar-${data.id}`,
        label: data.name,
        isShowNewBadge: data.meta?.newBadge ?? false,
    };

    return (
        <NavBarItemDropdownMenu {...prop}>
            <div className="dropdown-default">
                <ul className="flex-column">
                    { data.children.map((x: any) => (
                        <li className="flex-item" key={x.id}>
                            <a className="nav-link" href={x.href}>
                                { x.name }
                            </a>
                        </li>
                    )) }
                </ul>
            </div>
        </NavBarItemDropdownMenu>
    );
}

const constructDropDownTwoColumns = (data: NavBarItem) => {
    const prop = {
        id: `nav-bar-${data.id}`,
        key: `nav-bar-${data.id}`,
        label: data.name,
        isShowNewBadge: data.meta?.newBadge ?? false,
    };

    return (
        <NavBarItemDropdownMenu {...prop}>
            <div className="dropdown-two-columns">
                <div className="navbar-component_prefix-buttons">
                    { constructPrefixNavButtonsWithIcon(data) }
                </div>
                <div className="content">
                    { data.children.map(x => {
                        if (x.style) {
                            switch(x.style) {
                                case 'multiple-rows':
                                    return constructNavMultipleColumnsWithHeading(x);
                            }
                        } else {
                            return constructNavColumnWithHeading(x);
                        }
                    })}
                </div>
                {  constructGallery(data) }
            </div>
        </NavBarItemDropdownMenu>
    );
};

const constructDropDownOneAfterAnother = (data: NavBarItem) => {
    const prop = {
        id: `nav-bar-${data.id}`,
        key: `nav-bar-${data.id}`,
        label: data.name,
        isShowNewBadge: data.meta?.newBadge ?? false,
    };

    const [activeTab, setActiveTab] = React.useState('');

    return (
        <NavBarItemDropdownMenu {...prop}>
            <div className="dropdown-one-after-another">
                <div className="content">
                    <ul className="flex-column">
                        { data.children.map((x: any) => {
                            if(x.children) {
                                return (
                                    <li className="flex-item" onMouseEnter={() => setActiveTab(x.id)} key={x.id}>
                                        <a className={`nav-link directory ${activeTab === x.id ? "active" : ""}`}>
                                            <div className="label">
                                                { x.name }
                                            </div>
                                            <div className="icon">
                                                <SVGTopNavDirectoryExpand />
                                            </div>
                                        </a>
                                    </li>
                                );
                            } else {
                                return (
                                    <li className="flex-item" key={x.id}>
                                        <a className="nav-link" href={x.href}>
                                            { x.name }
                                        </a>
                                    </li>
                                );
                            }
                        }) }
                    </ul>
                </div>
                <div className="dropdown-menu tab-content">
                    <div className="tab-contentt">
                    {
                        data.children.filter(x => x.children).map((x: any) => {
                            return (
                                <div key={x.id} className={`tab-pane ${activeTab === x.id ? "active" : ""}`} id={x.id}>
                                    <ul className="flex-column">
                                        {
                                            x.children.map(y => (
                                                <li className="flex-item" key={y.id}>
                                                    <a className="nav-link" href={y.href}>
                                                        <div className="label">
                                                            { y.name }
                                                        </div>
                                                    </a>
                                                </li>
                                            ))
                                        }
                                        {
                                             x.corner && (
                                                <li className="corner-item" key={x.corner.id}>
                                                    <a className="nav-link" href={x.corner.href}>
                                                        { x.corner.name }
                                                    </a>
                                                </li>
                                            )
                                        }
                                    </ul>
                                </div>
                            );
                        })
                    }
                    </div>

                </div>
            </div>
        </NavBarItemDropdownMenu>
    );
};

export default constructDesktopNavBarItem;